import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw, Check, Mail, Send, ChevronRight, FileWarning } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Waivers — owner round-up of clients NOT on the current member waiver
 * (never signed, or signed the booking / an older version). One-tap to email
 * everyone a link to officially sign the current version; per-row resend and a
 * link into each profile. Sachin 2026-09.
 */
export default function Waivers() {
    const [data, setData] = useState(null);
    const [sendingAll, setSendingAll] = useState(false);
    const [busyId, setBusyId] = useState(null);

    const load = () => {
        axios.get(`${API}/coach/waivers/needs-signature`, { withCredentials: true })
            .then((r) => setData(r.data))
            .catch((e) => {
                toast.error("Couldn't load waivers", { description: e?.response?.data?.detail || "Try again." });
                setData({ clients: [], count: 0 });
            });
    };
    useEffect(() => { load(); }, []);

    const sendAll = async () => {
        if (!data?.count) return;
        if (!window.confirm(`Email ${data.count} client${data.count === 1 ? "" : "s"} a link to sign the current waiver?`)) return;
        setSendingAll(true);
        try {
            const r = await axios.post(`${API}/coach/waivers/request-signatures`, {}, { withCredentials: true });
            toast.success(`Sent ${r.data?.sent || 0} request${r.data?.sent === 1 ? "" : "s"}`, {
                description: [
                    r.data?.failed ? `${r.data.failed} failed` : null,
                    r.data?.no_email ? `${r.data.no_email} had no email` : null,
                ].filter(Boolean).join(" · ") || "All sent",
            });
            load();
        } catch (e) {
            toast.error("Send failed", { description: e?.response?.data?.detail || "Try again." });
        } finally {
            setSendingAll(false);
        }
    };

    const sendOne = async (c) => {
        setBusyId(c.user_id);
        try {
            const r = await axios.post(`${API}/coach/waivers/request-signature/${c.user_id}`, {}, { withCredentials: true });
            toast.success("Request sent", { description: r.data?.sent_to });
            load();
        } catch (e) {
            toast.error("Couldn't send", { description: e?.response?.data?.detail || "Try again." });
        } finally {
            setBusyId(null);
        }
    };

    if (data === null) {
        return <div className="flex items-center justify-center p-8 text-zinc-400"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…</div>;
    }
    const clients = data.clients || [];

    return (
        <div data-testid="waivers-page" className="max-w-3xl mx-auto p-6">
            <div className="mb-6 flex items-start justify-between gap-3">
                <div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red mb-1">Get everyone officially signed</div>
                    <h1 className="font-display text-2xl tracking-wider">Waivers</h1>
                    <p className="text-xs text-zinc-400 mt-1 max-w-xl">
                        Clients who aren't on the current member waiver ({data.current_version}) — never signed, or signed only the
                        booking/an older version. Email them a link to officially sign; your roster updates as they do.
                    </p>
                </div>
                <button type="button" data-testid="waivers-refresh" onClick={load}
                    className="text-[10px] tracking-[0.15em] uppercase inline-flex items-center gap-1.5 border border-zinc-700 text-zinc-300 hover:border-brand-red px-2.5 py-1.5">
                    <RefreshCw className="w-3 h-3" /> Refresh
                </button>
            </div>

            <div className="mb-5 flex items-center justify-between gap-3 flex-wrap">
                <div data-testid="waivers-summary" className="text-[11px] tracking-[0.15em] uppercase text-zinc-400">
                    {data.count} need signing · {data.missing_count ?? 0} never signed · {data.outdated_count ?? 0} signed older/booking
                </div>
                {clients.length > 0 && (
                    <button type="button" data-testid="waivers-send-all" onClick={sendAll} disabled={sendingAll}
                        className="flex items-center gap-2 bg-brand-red hover:bg-red-700 disabled:opacity-50 rounded px-4 py-2 text-sm font-semibold">
                        {sendingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Send request to all ({data.count})
                    </button>
                )}
            </div>

            {clients.length === 0 ? (
                <div data-testid="waivers-empty" className="border border-emerald-800/50 bg-emerald-950/20 rounded-xl p-5 text-emerald-300 text-sm flex items-center gap-2">
                    <Check className="w-4 h-4" /> Everyone is on the current waiver. 🎉
                </div>
            ) : (
                <div className="space-y-2">
                    {clients.map((c) => (
                        <div key={c.user_id} data-testid={`waiver-client-${c.user_id}`}
                            className="border border-zinc-800 rounded-lg bg-zinc-950/50 flex items-center justify-between gap-3 px-4 py-3">
                            <Link to={`/coach/leads/${c.user_id}`} data-testid={`waiver-open-${c.user_id}`} className="min-w-0 group">
                                <div className="text-sm text-white truncate group-hover:text-brand-red transition-colors inline-flex items-center gap-1.5">
                                    {c.name} <ChevronRight className="w-3 h-3 text-zinc-600" />
                                </div>
                                <div className="text-[10px] text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
                                    {c.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>}
                                    <span className={`inline-flex items-center gap-1 ${c.status === "missing" ? "text-red-400" : "text-amber-400"}`}>
                                        <FileWarning className="w-3 h-3" />
                                        {c.status === "missing" ? "Never signed" : `Signed ${c.signed_source === "assessment_booking" ? "booking waiver" : (c.signed_version || "older version")}`}
                                    </span>
                                    {c.last_requested_at && <span className="text-zinc-600">· requested {new Date(c.last_requested_at).toLocaleDateString()}</span>}
                                </div>
                            </Link>
                            <button type="button" data-testid={`waiver-send-${c.user_id}`} onClick={() => sendOne(c)} disabled={busyId === c.user_id}
                                className="text-[10px] tracking-[0.15em] uppercase inline-flex items-center gap-1.5 border border-zinc-700 text-zinc-200 hover:border-brand-red px-2.5 py-1.5 flex-shrink-0 disabled:opacity-50">
                                {busyId === c.user_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Send
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
